import { I18nThemed } from 'interfaces/mixins';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import { DNode, WidgetProperties } from '@dojo/framework/core/interfaces';
import { Store } from '@dojo/framework/stores/Store';
import { Messages } from '@dojo/framework/i18n/i18n';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import has from '@dojo/framework/core/has';

import Button from '@dojo/widgets/button';
import { CountdownTimer } from '../widgets/countdown-timer';

import { lccstore } from 'lcc-state';

import { QuizMeta, Question } from '../interfaces';

import css from '../css/prometric-review.m.css';

export interface PearsonVueReviewProperties {
	quizstore: Store<QuizMeta>;
	isaudit: boolean;
	loaded?: boolean;
	localizedBundle: { isPlaceholder: boolean; format: any; messages: Messages };

	getQuestions: () => Question[];
	onFinish: () => void;
	onReview: (flag: boolean) => void;
	filterOn: (name: 'marked' | 'complete' | 'incomplete' | 'all' | number) => void;
	checkFor: (name: 'marked' | 'complete' | 'incomplete' | 'all') => boolean;
}

@theme(css)
export class PearsonVueReview extends I18nThemed<PearsonVueReviewProperties> {
	private areyousure: boolean = false;
	private data: Question[] = [];
	private _inited: boolean = false;

	protected onAttach(): void {
		lccstore.onChange(lccstore.path('dynls'), () => this.invalidate());
	}

	@beforeProperties()
	ensure_nls(
		properties: PearsonVueReviewProperties & WidgetProperties
	): PearsonVueReviewProperties & WidgetProperties {
		const { get, path } = lccstore;
		const dynls = get(path('dynls'));
		const bundle = dynls.get('quiz');
		if (!bundle) {
			properties.localizedBundle = { isPlaceholder: true, format: null, messages: {} as Messages };
		} else {
			properties.localizedBundle = this.localizeBundle(bundle);
		}
		(() => {
			const { quizstore } = properties;
			const { get, path } = quizstore;
			properties.loaded = get(path('loaded'));
			if (!this._inited) {
				const invalidatePaths = [['loaded']].map((path: any) => quizstore.path(path.join('/')));
				const handle = quizstore.onChange(invalidatePaths, () => this.invalidate());
				this.own({
					destroy: () => {
						handle.remove();
					}
				});
				this._inited = true;
			}
		})();
		return properties;
	}

	protected render_heads(): DNode {
		const {
			isaudit,
			localizedBundle: {
				messages: {
					question: questionText = '',
					incorrect: incorrectText = '',
					marked: markedText = '',
					status: statusText = ''
				}
			}
		} = this.properties;

		return v(
			'div',
			{ key: 'head-block', role: 'rolegroup', classes: [this.theme(css.tableHead), this.theme(css.row)] },
			[
				v('div', { role: 'row' }, [
					v(
						'div',
						{
							key: 'head-question',
							role: 'columnheader',
							'aria-sort': 'none',
							classes: [this.theme(css.question)]
						},
						[questionText]
					),
					v(
						'div',
						{
							key: 'head-marked',
							role: 'columnheader',
							'aria-sort': 'none',
							classes: [this.theme(css.marked)]
						},
						[isaudit ? incorrectText : markedText]
					),
					v(
						'div',
						{
							key: 'head-status',
							role: 'columnheader',
							'aria-sort': 'none',
							classes: [this.theme(css.status)]
						},
						[statusText]
					)
				])
			]
		);
	}

	protected render_row(question: Question): DNode {
		const {
			isaudit,
			filterOn,
			onReview,
			localizedBundle: {
				messages: {
					question: questionText = '',
					incorrect: incorrectText = '',
					marked: markedText = '',
					status_complete = '[quiz] status_complete',
					status_incomplete = '[quiz] status_incomplete'
				}
			}
		} = this.properties;
		const { cursor, marked = false, flags } = question;
		const isOdd = !!(cursor % 2);
		const isComplete = flags[1];
		const isMarked = marked;
		return v(
			'a',
			{
				key: `row-${cursor}`,
				role: 'row',
				href: 'javascript:void;',
				classes: [this.theme(css.row), isOdd ? this.theme(css.oddRow) : null],
				onclick: (event: any) => {
					event && event.stopPropagation();
					filterOn(cursor);
					onReview(false);
				}
			},
			[
				v('div', { key: `q-${cursor}`, role: 'cell', classes: [this.theme(css.question)] }, [
					`${questionText} ${cursor + 1}`
				]),
				v(
					'div',
					{
						id: isaudit ? incorrectText : markedText,
						key: `mrk-${cursor}`,
						role: 'cell',
						classes: [this.theme(css.marked)]
					},
					[
						isMarked
							? v('i', {
									...formatAriaProperties({ labelledBy: isaudit ? incorrectText : markedText }),
									classes: ['fa', 'fa-check']
							  })
							: v('i', {}, [' '])
					]
				),
				v(
					'div',
					{
						key: `cmp-${cursor}`,
						role: 'cell button',
						classes: [
							this.theme(css.status),
							isComplete ? this.theme(css.complete) : this.theme(css.incomplete)
						]
					},
					[
						isComplete
							? v('i', {
									...formatAriaProperties({ label: status_complete }),
									classes: ['fa', 'fa-circle']
							  })
							: v('i', {
									...formatAriaProperties({ label: status_incomplete }),
									classes: ['fa', 'fa-exclamation-circle']
							  })
					]
				)
			]
		);
	}

	protected render_buttons(): DNode[] {
		const {
			quizstore,
			onFinish,
			filterOn,
			checkFor,
			onReview,
			isaudit,
			localizedBundle: {
				messages: {
					review_all = '',
					review_incorrect = '',
					review_marked = '',
					review_incomplete = '',
					end_review = ''
				}
			}
		} = this.properties;

		return [
			v('div', { classes: [this.theme(css.oneaction)] }, [
				w(
					Button,
					{
						key: 'all',
						onClick: () => {
							filterOn('all');
							onReview(false);
						}
					},
					[review_all]
				)
			]),
			v('div', { classes: [this.theme(css.oneaction)] }, [
				w(
					Button,
					{
						key: 'marked',
						disabled: !checkFor('marked'),
						onClick: () => {
							filterOn('marked');
							onReview(false);
						}
					},
					[isaudit ? review_incorrect : review_marked]
				)
			]),
			v('div', { classes: [this.theme(css.oneaction)] }, [
				w(
					Button,
					{
						key: 'incomplete',
						disabled: !checkFor('incomplete'),
						onClick: () => {
							filterOn('incomplete');
							onReview(false);
						}
					},
					[review_incomplete]
				)
			]),
			v('div', { classes: [this.theme(css.oneaction), this.theme(css.primaryButton)] }, [
				w(
					Button,
					{
						key: 'finish',
						onClick: () => {
							if (isaudit) {
								onFinish();
							} else {
								this.areyousure = true;
								this.invalidate();
							}
						}
					},
					[end_review]
				)
			]),
			v('div', { key: 'timer', classes: [this.theme(css.timerblock)] }, [
				!isaudit
					? v('div', { classes: [this.theme(css.timer)] }, [w(CountdownTimer, { quizstore, countdown: '' })])
					: null
			])
		];
	}

	protected areYouSure(): DNode[] {
		const {
			onFinish,
			localizedBundle: {
				messages: { ays_title = '', ays_body = '', ays_cancel = '', submit: submitText = '' }
			}
		} = this.properties;

		return [
			v('div', { classes: [this.theme(css.areYouSure)] }, [
				v('h2', { classes: [this.theme(css.sureHeading)] }, [ays_title]),
				v('div', { key: '1', classes: [this.theme(css.sureBody)] }, [ays_body]),
				v('div', { key: '2', classes: [this.theme(css.sureButtons)] }, [
					w(
						Button,
						{
							key: 'ays-cancel',
							onClick: () => {
								this.areyousure = false;
								this.invalidate();
							}
						},
						[ays_cancel]
					),
					w(
						Button,
						{
							key: 'ays-finish',
							onClick: () => {
								onFinish();
							}
						},
						[submitText]
					)
				])
			])
		];
	}

	protected render_micro_review(): DNode {
		const {
			isaudit,
			filterOn,
			onReview,
			localizedBundle: {
				messages: {
					question: questionText = '',
					incorrect: incorrectText = '',
					marked: markedText = '',
					status: statusText = '',
					status_complete = '[quiz] status_complete',
					status_incomplete = '[quiz] status_incomplete'
				}
			}
		} = this.properties;

		const kids = this.data.map((question) => {
			const { cursor, marked = false, flags } = question;
			const isComplete = flags[1];
			const isMarked = marked;
			return v(
				'tr',
				{
					key: `row-${cursor}`,
					tabindex: '0',
					onclick: (event: any) => {
						event && event.stopPropagation();
						filterOn(cursor);
						onReview(false);
					}
				},
				[
					v('td', { key: `q-${cursor}` }, [`${questionText} ${cursor + 1}`]),
					v('td', { key: `mrk-${cursor}` }, [
						isMarked
							? v('span', { classes: [this.theme(css.pill)] }, [
									v('i', { classes: ['far', 'fa-flag'] }),
									isaudit ? incorrectText : markedText
							  ])
							: null
					]),
					v('td', { key: `cmp-${cursor}` }, [
						isComplete
							? v('img', {
									src: `${window.hcstatic.portal_cdn}/js/images/icons/circle-check.svg`,
									alt: status_complete,
									title: status_complete
							  })
							: v('img', {
									src: `${window.hcstatic.portal_cdn}/js/images/icons/alert-triangle.svg`,
									alt: status_incomplete,
									title: status_incomplete
							  })
					])
				]
			);
		});

		return v('div', { classes: [this.theme(css.microReview)] }, [
			v('table', { xqa: 'micro-review-table' }, [
				v('thead', { key: 'head-block' }, [
					v('tr', { xqa: 'micro-review-head-row' }, [
						v(
							'th',
							{
								key: 'head-question'
							},
							[questionText]
						),
						v(
							'th',
							{
								key: 'head-marked'
							},
							[isaudit ? incorrectText : markedText]
						),
						v(
							'th',
							{
								key: 'head-status'
							},
							[statusText]
						)
					])
				]),
				v('tbody', { key: 'body-block' }, [...kids])
			])
		]);
	}

	protected render_review(): DNode[] {
		const {
			getQuestions,
			loaded = false,
			localizedBundle: {
				messages: { review_instructions = '' }
			}
		} = this.properties;

		if (!loaded) {
			v('div', { key: 'loading', classes: [this.theme(css.loading)] }, [
				v('i', { classes: ['fas', 'fa-spinner', 'fa-spin'] })
			]);
		}

		if (this.data.length === 0) {
			this.data = getQuestions();
		}
		const { data } = this;
		const kids = data.map<DNode>((q) => this.render_row(q));

		return [
			v('div', { key: 'buttons', classes: [this.theme(css.buttons)] }, this.render_buttons()),
			v('div', { key: 'instruct', classes: [this.theme(css.instruct)] }, [review_instructions]),
			has('use-micro-view')
				? this.render_micro_review()
				: v('div', { key: 'review-table', role: 'table', classes: [this.theme(css.table)], tabindex: '0' }, [
						this.render_heads(),
						v('div', { role: 'rolegroup' }, [...kids])
				  ])
		];
	}

	protected render(): DNode {
		const { isPlaceholder } = this.properties.localizedBundle;
		if (isPlaceholder) {
			return null;
		}
		const { areyousure } = this;
		const { isaudit } = this.properties;
		const xqa = `review ${isaudit ? 'audit' : 'quiz'}`;

		return v(
			'div',
			{ key: 'review', xqa, classes: [this.theme(css.root)] },
			areyousure ? this.areYouSure() : this.render_review()
		);
	}
}

export default PearsonVueReview;
